import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatAED, buildWhatsAppLink, resolveImg } from "../lib/api";
import { toast } from "sonner";
import { useT } from "../lib/i18n";
import useSEO from "../lib/useSEO";
import { propertyLd, breadcrumbLd } from "../lib/jsonld";

export default function PropertyDetail() {
  const { t } = useT();
  const { id } = useParams();
  const navigate = useNavigate();
  const [prop, setProp] = React.useState(null);
  const [activeImg, setActiveImg] = React.useState(0);
  const [form, setForm] = React.useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    api.get(`/properties/${id}`).then((r) => setProp(r.data)).catch(() => {
      toast.error("Property not found");
      navigate("/properties");
    });
  }, [id, navigate]);

  const jsonLd = React.useMemo(() => {
    if (!prop) return [];
    return [
      propertyLd(prop),
      breadcrumbLd([
        { name: "Home", url: "/" },
        { name: "Properties", url: "/properties" },
        { name: prop.title, url: `/properties/${prop.id}` },
      ]),
    ];
  }, [prop]);

  useSEO({
    title: prop ? `${prop.title} — ${prop.zone} | WarehousesUAE` : "Property — WarehousesUAE",
    description: prop ? (prop.description || `${prop.title} for rent in ${prop.zone}, Dubai. ${prop.size_sqft?.toLocaleString?.() || prop.size_sqft} sqft. Annual rent AED ${formatAED(prop.price_aed)}.`).slice(0, 300) : "",
    image: prop && prop.images?.[0] ? resolveImg(prop.images[0]) : window.location.origin + "/og-default.png",
    canonical: window.location.origin + `/properties/${id}`,
    jsonLd,
  });

  if (!prop) {
    return (
      <div className="min-h-screen flex items-center justify-center font-mono-tech text-sm text-zinc-500">
        Loading inventory<span className="cursor-blink"></span>
      </div>
    );
  }

  const submitEnquiry = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post("/enquiries", {
        ...form,
        property_id: prop.id,
        property_ref: prop.reference,
        source: "website",
      });
      toast.success(t("toast.enq_success"));
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
      const url = res?.data?.whatsapp_redirect_url;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error(t("toast.enq_fail"));
    } finally {
      setSubmitting(false);
    }
  };

  const waMsg = `Hello WarehousesUAE, I'd like to enquire about ${prop.reference} — ${prop.title}.`;

  return (
    <div data-testid="property-detail-page" className="bg-zinc-50 min-h-screen">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-3 font-mono-tech text-[10px] uppercase text-zinc-500">
          <Link to="/" className="hover:text-zinc-900">Home</Link> / <Link to="/properties" className="hover:text-zinc-900">Properties</Link> / <span className="text-zinc-900">{prop.reference}</span>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8 grid lg:grid-cols-12 gap-8">
        {/* Gallery */}
        <div className="lg:col-span-8">
          <div className="aspect-[16/10] bg-zinc-200 mb-3 overflow-hidden border border-zinc-200">
            <img src={resolveImg(prop.images?.[activeImg] || prop.images?.[0])} alt={prop.title} className="w-full h-full object-cover" />
          </div>
          {prop.images?.length > 1 && (
            <div className="grid grid-cols-5 gap-2">
              {prop.images.map((img, i) => (
                <button
                  key={i}
                  data-testid={`gallery-thumb-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-square overflow-hidden border-2 ${i === activeImg ? "border-[#031833]" : "border-transparent"}`}
                >
                  <img src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* Specs */}
          <div className="mt-8 bg-white border border-zinc-200">
            <div className="border-b border-zinc-200 px-5 py-3 flex items-center justify-between">
              <span className="font-mono-tech text-[10px] uppercase text-[#031833]">// SPECIFICATIONS</span>
              <span className="font-mono-tech text-[10px] uppercase text-zinc-500">{prop.reference}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
              {[
                { l: "Property Type", v: prop.property_type.replace("_", " ") },
                { l: "Total Area", v: `${formatAED(prop.size_sqft)} sqft` },
                { l: "Clear Height", v: prop.height_meters ? `${prop.height_meters} m` : "—" },
                { l: "Loading Bays", v: prop.loading_bays || "—" },
                { l: "Power Supply", v: prop.power_kw ? `${prop.power_kw} kW` : "—" },
                { l: "Parking", v: prop.parking || "—" },
                { l: "Chiller", v: prop.has_chiller ? "Yes" : "No" },
                { l: "Office", v: prop.has_office ? "Included" : "No" },
              ].map((s, i) => (
                <div key={i} className="border-t border-r border-zinc-100 p-4">
                  <div className="font-mono-tech text-[9px] uppercase text-zinc-500 mb-1">{s.l}</div>
                  <div className="font-mono-tech text-sm font-semibold capitalize">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Description */}
          <div className="mt-8 bg-white border border-zinc-200 p-6">
            <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-3">// DESCRIPTION</div>
            <p className="text-zinc-700 leading-relaxed">{prop.description}</p>

            {prop.features?.length > 0 && (
              <>
                <div className="font-mono-tech text-[10px] uppercase text-[#031833] mt-6 mb-3">// FEATURES</div>
                <div className="flex flex-wrap gap-2">
                  {prop.features.map((f) => (
                    <span key={f} className="border border-zinc-300 px-3 py-1 font-mono-tech text-xs">{f}</span>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Map embed */}
          <div className="mt-8 bg-white border border-zinc-200">
            <div className="border-b border-zinc-200 px-5 py-3 font-mono-tech text-[10px] uppercase text-[#031833]">
              // LOCATION — {prop.address}
            </div>
            <iframe
              title="map"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(prop.address + " Dubai")}&t=&z=13&ie=UTF8&iwloc=&output=embed`}
              className="w-full h-[320px] border-0"
            ></iframe>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-4 space-y-4">
          <div className="bg-white border border-zinc-900" style={{ boxShadow: "6px 6px 0 0 #031833" }}>
            <div className="p-5 border-b border-zinc-200">
              {prop.rent_sale === "sale" && (
                <div data-testid="badge-for-sale-detail" className="inline-block bg-[#FFCC00] text-[#031833] font-mono-tech text-[10px] uppercase px-2 py-0.5 mb-2 tracking-wider">
                  FOR SALE
                </div>
              )}
              <div className="font-mono-tech text-[10px] uppercase text-zinc-500 mb-1">{prop.zone}</div>
              <h1 data-testid="property-title" className="font-display text-2xl md:text-3xl tracking-tighter leading-tight">{prop.title}</h1>
              <div className="mt-4 flex items-baseline gap-2">
                <div className="font-display text-4xl tracking-tighter">AED {formatAED(prop.price_aed)}</div>
                <div className="font-mono-tech text-xs text-zinc-500">
                  {prop.rent_sale === "sale" ? (() => {
                    const ownership = (prop.features || [])
                      .map((f) => /ownership\s*:\s*(.+)/i.exec(f)?.[1]?.trim())
                      .find(Boolean);
                    return ownership ? ownership.toLowerCase() : "sale price";
                  })() : "/year"}
                </div>
              </div>
              <div className="font-mono-tech text-[10px] uppercase text-zinc-500 mt-1">
                AED {prop.price_per_sqft}/sqft · {formatAED(prop.size_sqft)} sqft
              </div>
            </div>

            <div className="p-5 space-y-3">
              <a
                href={buildWhatsAppLink(waMsg)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="detail-whatsapp-btn"
                className="block bg-[#25D366] text-white font-mono-tech text-sm uppercase text-center py-3 hover:bg-[#1eb955]"
              >
                ▶ WHATSAPP ENQUIRY
              </a>
              <a
                href="tel:+971588320669"
                data-testid="detail-call-btn"
                className="block bg-zinc-900 text-white font-mono-tech text-sm uppercase text-center py-3 hover:bg-zinc-800"
              >
                CALL +971 58 832 0669
              </a>
            </div>
          </div>

          {/* Enquiry form */}
          <div className="bg-white border border-zinc-200 p-5">
            <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-3">// REQUEST VIEWING</div>
            <form onSubmit={submitEnquiry} data-testid="enquiry-form" className="space-y-3">
              <input data-testid="enq-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="w-full border border-zinc-300 px-3 py-2 font-mono-tech text-sm outline-none focus:border-[#031833]" />
              <input data-testid="enq-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full border border-zinc-300 px-3 py-2 font-mono-tech text-sm outline-none focus:border-[#031833]" />
              <input data-testid="enq-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (+971...)" className="w-full border border-zinc-300 px-3 py-2 font-mono-tech text-sm outline-none focus:border-[#031833]" />
              <input data-testid="enq-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Company (optional)" className="w-full border border-zinc-300 px-3 py-2 font-mono-tech text-sm outline-none focus:border-[#031833]" />
              <textarea data-testid="enq-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Your requirements..." className="w-full border border-zinc-300 px-3 py-2 font-mono-tech text-sm outline-none focus:border-[#031833]"></textarea>
              <button
                data-testid="enq-submit"
                disabled={submitting}
                className="w-full bg-[#031833] text-white font-mono-tech text-sm uppercase py-3 disabled:opacity-50 hover:bg-[#0A2455]"
              >
                {submitting ? "SUBMITTING..." : "SUBMIT ENQUIRY →"}
              </button>
            </form>
          </div>
        </aside>
      </div>
    </div>
  );
}
